import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = "/api";

const LABEL_COLORS = {
  Excellent: '#4ade80',
  Good: '#AFA9EC',
  Fair: '#fbbf24',
  Poor: '#f87171',
  'No Data': '#6b7280',
};

function fmt(value, digits = 2) {
  if (value == null) return '—';
  return value.toFixed(digits);
}

function fmtDate(iso) {
  if (!iso) return '—';
  // Expect YYYY-MM-DD
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function ScoreBadge({ value }) {
  if (value == null) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  let color = '#6b7280';
  if (value >= 8.0) color = LABEL_COLORS.Excellent;
  else if (value >= 6.0) color = LABEL_COLORS.Good;
  else if (value >= 4.0) color = LABEL_COLORS.Fair;
  else color = LABEL_COLORS.Poor;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
        {value.toFixed(2)}
      </span>
      <span
        aria-hidden="true"
        style={{
          width: 8, height: 8, borderRadius: '50%',
          background: color, boxShadow: `0 0 0 2px ${color}22`,
        }}
      />
    </span>
  );
}

function MetricCard({ label, weight, value }) {
  return (
    <div style={{ flex: 1, textAlign: 'center', padding: '10px 0' }}>
      <div style={{
        fontSize: 11, color: 'var(--text-secondary)',
        textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
      }}>
        {label} <span style={{ opacity: 0.6 }}>({weight}%)</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>
        {fmt(value)}
      </div>
    </div>
  );
}

export default function ValidationDashboard() {
  const { authFetch, user, logout } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState(null);    // null = loading, [] = empty
  const [history, setHistory] = useState(null);
  const [validating, setValidating] = useState(null); // snapshot_id in corso
  const [error, setError] = useState('');

  const loadPending = useCallback(() => {
    authFetch(`${API}/admin/validation/pending`)
      .then(r => r.json())
      .then(setPending)
      .catch(() => setPending([]));
  }, [authFetch]);

  const loadHistory = useCallback(() => {
    authFetch(`${API}/admin/validation/history`)
      .then(r => r.json())
      .then(setHistory)
      .catch(() => setHistory([]));
  }, [authFetch]);

  useEffect(() => {
    loadPending();
    loadHistory();
  }, [loadPending, loadHistory]);

  const handleValidate = async (snapshotId) => {
    setError('');
    setValidating(snapshotId);
    try {
      const res = await authFetch(
        `${API}/admin/validation/snapshots/${snapshotId}/validate`,
        { method: 'POST', body: JSON.stringify({}) }
      );
      if (!res.ok) {
        const msg = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${msg.slice(0, 120)}`);
      }
      loadPending();
      loadHistory();
    } catch (e) {
      setError(`Validazione fallita: ${e.message || e}`);
    } finally {
      setValidating(null);
    }
  };

  const hero = history && history.length > 0 ? history[0] : null;

  return (
    <div className="admin-page">
      <header className="header">
        <div className="header-left">
          <span className="logo-text" style={{ cursor: 'pointer' }} onClick={() => navigate('/chat')}>
            ERMENE
          </span>
          <span className="logo-subtitle">Model Health</span>
        </div>
        <div className="header-right">
          <span className="header-subtitle">{user?.username}</span>
          <button className="theme-toggle" onClick={logout}>Esci</button>
        </div>
      </header>

      <div className="admin-content" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* SEZIONE 1 — Hero: ultima verifica */}
        <section className="val-reliability" aria-label="Ultima verifica">
          {hero ? (
            <>
              <div style={{ marginBottom: 16 }}>
                <div style={{
                  fontSize: 11, color: 'var(--text-secondary)',
                  textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6,
                }}>
                  Ultima verifica
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.1 }}>
                  Validato il {fmtDate(hero.validation_date)}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                  sullo stato del modello congelato al {fmtDate(hero.prediction_date)}
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <span
                  className="val-label-badge"
                  style={{
                    background: (LABEL_COLORS[hero.reliability_label] || '#6b7280') + '22',
                    color: LABEL_COLORS[hero.reliability_label] || '#6b7280',
                  }}
                >
                  {hero.reliability_label}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                <MetricCard label="Accuracy" weight={50} value={hero.avg_accuracy_score} />
                <MetricCard label="Calibration" weight={30} value={hero.avg_calibration_score} />
                <MetricCard label="Timeliness" weight={20} value={hero.avg_timeliness_score} />
                <MetricCard label="Overall" weight={100} value={hero.avg_overall_score} />
              </div>
            </>
          ) : history === null ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>
              Caricamento…
            </div>
          ) : (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>
              Nessuna validazione ancora eseguita.
            </div>
          )}
        </section>

        {error && (
          <div
            role="alert"
            style={{
              padding: '10px 14px', borderRadius: 8,
              background: 'rgba(248, 113, 113, 0.15)',
              border: '1px solid rgba(248, 113, 113, 0.4)',
              color: '#f87171', fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {/* SEZIONE 2 — Snapshot da validare */}
        <section aria-label="Snapshot da validare">
          <h2 style={{
            fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
            textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10,
          }}>
            Snapshot da validare
          </h2>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Data T0</th>
                  <th>Accuracy</th>
                  <th>Calibration</th>
                  <th>Timeliness</th>
                  <th>Score</th>
                  <th style={{ textAlign: 'right' }}>Azione</th>
                </tr>
              </thead>
              <tbody>
                {pending === null && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 16 }}>Caricamento…</td></tr>
                )}
                {pending && pending.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 16 }}>
                    Nessuno snapshot in attesa di validazione.
                  </td></tr>
                )}
                {pending && pending.map((p, i) => {
                  const isFirst = i === 0;
                  const isThisValidating = validating === p.snapshot_id;
                  return (
                    <tr key={p.snapshot_id}>
                      <td style={{ fontWeight: 500 }}>{fmtDate(p.prediction_date)}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>—</td>
                      <td style={{ color: 'var(--text-secondary)' }}>—</td>
                      <td style={{ color: 'var(--text-secondary)' }}>—</td>
                      <td style={{ color: 'var(--text-secondary)' }}>—</td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                          <button
                            onClick={() => handleValidate(p.snapshot_id)}
                            disabled={!isFirst || validating !== null}
                            style={{
                              padding: '4px 14px', fontSize: 12, fontWeight: 600,
                              background: isFirst ? 'var(--accent-bright)' : 'var(--surface-raised)',
                              color: isFirst ? 'var(--bg)' : 'var(--text-secondary)',
                              border: 'none', borderRadius: 6,
                              cursor: (isFirst && !validating) ? 'pointer' : 'not-allowed',
                              opacity: isFirst ? 1 : 0.5,
                            }}
                          >
                            {isThisValidating ? 'Validazione in corso…' : 'Valida'}
                          </button>
                          {isFirst && !isThisValidating && (
                            <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>
                              Verifica lo snapshot del {fmtDate(p.prediction_date)} con gli ultimi dati caricati
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* SEZIONE 3 — Storico validazioni */}
        <section aria-label="Storico validazioni">
          <h2 style={{
            fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
            textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10,
          }}>
            Storico validazioni
          </h2>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Data T0</th>
                  <th>Accuracy</th>
                  <th>Calibration</th>
                  <th>Timeliness</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {history === null && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 16 }}>Caricamento…</td></tr>
                )}
                {history && history.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 16 }}>
                    Nessuna validazione storica.
                  </td></tr>
                )}
                {history && history.map(h => (
                  <tr key={h.snapshot_id}>
                    <td style={{ fontWeight: 500 }}>{fmtDate(h.prediction_date)}</td>
                    <td style={{ fontSize: 13 }}>{fmt(h.avg_accuracy_score)}</td>
                    <td style={{ fontSize: 13 }}>{fmt(h.avg_calibration_score)}</td>
                    <td style={{ fontSize: 13 }}>{fmt(h.avg_timeliness_score)}</td>
                    <td><ScoreBadge value={h.avg_overall_score} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

      </div>
    </div>
  );
}
